import { Badge } from "@/components/ui/Badge";
import type {
  CurationCellStatus,
  CurationVerificationStatus,
} from "@/features/data-curation/types";
import type { ReactNode } from "react";

const statusClass: Record<CurationCellStatus, string> = {
  pending: "border-amber-200 bg-amber-50 text-amber-800",
  verified: "border-emerald-200 bg-emerald-50 text-emerald-800",
  matched: "border-blue-300 bg-blue-100 text-blue-900",
  explained: "border-blue-200 bg-blue-50 text-blue-800",
  rejected: "border-red-200 bg-red-50 text-red-800",
};

const statusName: Record<CurationCellStatus, string> = {
  pending: "Pending",
  verified: "Verified",
  matched: "Matched the record",
  explained: "Explained",
  rejected: "Rejected",
};

/** The row edge. Pending is dashed so that color is not the only difference. */
const barClass: Record<CurationVerificationStatus, string> = {
  verified: "border-solid border-emerald-600",
  explained: "border-solid border-blue-600",
  pending: "border-dashed border-amber-600",
  rejected: "border-solid border-red-600",
};

/**
 * The corner mark of a cell. Only explained (blue) and rejected (red) cells have
 * one; a rejected cell also has a red background, so the color of the mark is
 * not the only difference. Verified, matched, pending, and not-found cells have none.
 */
const cornerClass: Partial<Record<CurationCellStatus, string>> = {
  explained: "fill-blue-600 stroke-blue-600",
  rejected: "fill-red-600 stroke-red-600",
};

const underlineClass: Partial<Record<CurationVerificationStatus, string>> = {
  explained: "underline decoration-solid decoration-2 underline-offset-2 decoration-blue-600",
  rejected: "underline decoration-solid decoration-2 underline-offset-2 decoration-red-600",
};

export const statusTitle: Record<CurationCellStatus, string> = {
  pending: "Pending: not verified yet",
  verified: "Verified",
  matched: "Matched the record: copied from the record of its source and checked against it",
  explained: "Explanation only — not automatically verified",
  rejected: "Rejected",
};

export const rowStatusTitle: Record<CurationVerificationStatus, string> = {
  verified: "Row status: all filled cells are verified",
  explained:
    "Row status: all filled cells pass; some are explanations, not checked automatically",
  pending: "Row status: a required cell is missing, or a cell is not checked yet",
  rejected: "Row status: a cell is rejected",
};

/** Whether every required cell of a row passes. */
export const rowPasses = (status: CurationVerificationStatus) =>
  status === "verified" || status === "explained";

/** The status of a row as a bar along the left edge of its name. The parent must be positioned. */
export const StatusBar = ({ status }: { status: CurationVerificationStatus }) => (
  <span
    aria-hidden
    data-status-bar={status}
    className={`pointer-events-none absolute inset-y-0 left-0 border-l-4 ${
      barClass[rowPasses(status) ? "verified" : status]
    }`}
  />
);

/**
 * The status of an explained or rejected cell as a triangle in its top left
 * corner, as spreadsheets mark a cell. Other cells get none. The parent must be
 * positioned.
 */
export const StatusCorner = ({ status }: { status: CurationCellStatus }) => {
  const markClass = cornerClass[status];
  if (!markClass) return null;
  return (
    <svg
      aria-hidden
      data-status-bar={status}
      viewBox="0 0 10 10"
      className="pointer-events-none absolute left-0 top-0 h-2.5 w-2.5"
    >
      <path
        d="M0.75 0.75H9.25L0.75 9.25Z"
        strokeWidth={1.5}
        strokeLinejoin="round"
        className={markClass}
      />
    </svg>
  );
};

/** One value of a cell, underlined in blue when explained and in red when rejected. */
export const StatusUnderline = ({
  status,
  children,
}: {
  status: CurationVerificationStatus;
  children: ReactNode;
}) => (
  <span title={statusTitle[status]} className={underlineClass[status]}>
    {children}
  </span>
);

const SOURCE_TEXT_NAME = "Matched source text";
const SOURCE_TEXT_TITLE =
  "Matched source text: the value is the exact text of the passage it came from";

/**
 * The status of a cell, segment or claim as a colored word. A verified Fast
 * value (sourceText) says that it matched its source text.
 */
export const StatusBadge = ({
  status,
  sourceText = false,
}: {
  status: CurationCellStatus;
  sourceText?: boolean;
}) => {
  const matched = sourceText && status === "verified";
  const name = matched ? SOURCE_TEXT_NAME : statusName[status];
  return (
    <Badge
      className={`shrink-0 border px-2 !py-0.5 text-xs leading-4 ${statusClass[status]}`}
      aria-label={`Status: ${name.toLowerCase()}`}
      title={matched ? SOURCE_TEXT_TITLE : statusTitle[status]}
    >
      {name}
    </Badge>
  );
};
